/**
 * cart-bundle-sync.js
 *
 * Maintains bundle integrity for cart line items that carry _bundleId / _role
 * properties (injected at add-to-cart time by the taste-profile / offer flows).
 *
 * Rules enforced on every sync:
 *   A. If a bundle has no "main" item → remove every "gift" item in that bundle.
 *   B. If a gift item has quantity > 1 → clamp it back to 1 (safety net; normal UX
 *      routes gift increments through /cart/add.js as paid lines — see fetch hook).
 *
 * Fetch hook (gift quantity > 1):
 *   When the shopper raises a bundle gift line above 1, we keep the gift row at
 *   quantity 1 and add (requestedQty − 1) units of the same variant with no line
 *   properties so they ring up at full price as separate cart lines.
 *
 * Items that do NOT have a _bundleId property are never touched.
 *
 * Public API (window.CartBundleSync):
 *   sync()              – run immediately, returns Promise<cart>
 *   debouncedSync()     – debounced wrapper, safe to call on every cart event
 *   groupByBundle(items)
 *   removeItem(key)
 *   updateQuantity(key, qty)
 */

(function () {
  'use strict';

  var DEBOUNCE_DELAY = 400; // ms

  var _syncing = false;
  var _debounceTimer = null;
  var _internalChange = false; // true while our own code is calling /cart/change.js

  // ---------------------------------------------------------------------------
  // Network helpers
  // ---------------------------------------------------------------------------

  /**
   * Fetch the current cart state.
   * @returns {Promise<Object>}
   */
  /**
   * Match a cart line by key, variant id, or 1-based line index (theme / Recharge).
   */
  function findCartItemById(cart, itemId) {
    if (!cart || !cart.items || itemId === undefined || itemId === null) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var ci = cart.items[i];
      if (
        ci.key === itemId ||
        String(ci.variant_id) === String(itemId) ||
        String(i + 1) === String(itemId)
      ) {
        return ci;
      }
    }
    return null;
  }

  function fetchCart() {
    return fetch('/cart.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    }).then(function (res) {
      if (!res.ok) throw new Error('[CartBundleSync] /cart.js ' + res.status);
      return res.json();
    });
  }

  /**
   * Collect section IDs expected by cart UI listeners.
   * Mirrors cart.js/cart-add-merge.js behavior.
   *
   * @returns {Array<string>}
   */
  function collectBundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sections } })
    );
    return sections;
  }

  /**
   * Keep cart badge stable even if payloads are partial.
   * Falls back to hiding badge when count is invalid.
   *
   * @param {number|string} count
   */
  function updateCartCountBadge(count) {
    var parsed = Number(count);
    var safeCount = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    document.querySelectorAll('cart-count').forEach(function (el) {
      el.innerText = String(safeCount);
      el.hidden = safeCount === 0;
    });
  }

  /**
   * Set a line item's quantity via its unique key.
   * Pass quantity: 0 to remove the item entirely.
   *
   * @param {string} key      – line_item.key (NOT variant id)
   * @param {number} quantity
   * @returns {Promise<Object>} updated cart
   */
  function _cartChange(key, quantity) {
    _internalChange = true;
    return fetch('/cart/change.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity }),
    }).then(function (res) {
      _internalChange = false;
      if (!res.ok) throw new Error('[CartBundleSync] /cart/change.js ' + res.status);
      return res.json();
    }).catch(function (err) {
      _internalChange = false;
      throw err;
    });
  }

  // ---------------------------------------------------------------------------
  // Exported helpers
  // ---------------------------------------------------------------------------

  /**
   * Group cart line items by _bundleId.
   * Only items with a non-empty _bundleId property are included.
   *
   * @param {Array} items – cart.items
   * @returns {Map<string, { mains: Array, gifts: Array }>}
   */
  function groupByBundle(items) {
    var groups = new Map();
    (items || []).forEach(function (item) {
      var props = item.properties || {};
      var bundleId = typeof props._bundleId === 'string' ? props._bundleId.trim() : '';
      if (!bundleId) return; // not part of a bundle — skip

      if (!groups.has(bundleId)) {
        groups.set(bundleId, { mains: [], gifts: [] });
      }

      var group = groups.get(bundleId);
      var role = typeof props._role === 'string' ? props._role.trim().toLowerCase() : '';

      if (role === 'main') {
        group.mains.push(item);
      } else if (role === 'gift') {
        group.gifts.push(item);
      }
      // items with an unrecognised / missing _role are ignored
    });
    return groups;
  }

  /**
   * Remove a line item (set quantity to 0).
   * @param {string} key
   * @returns {Promise<Object>}
   */
  function removeItem(key) {
    return _cartChange(key, 0);
  }

  /**
   * Update a line item's quantity.
   * @param {string} key
   * @param {number} quantity
   * @returns {Promise<Object>}
   */
  function updateQuantity(key, quantity) {
    return _cartChange(key, quantity);
  }

  /**
   * Remove a main bundle item AND all gift items that share the same _bundleId.
   *
   * Use this instead of removeItem() when the user removes a main product so
   * that orphaned gift items are cleaned up immediately rather than waiting for
   * the next debounced sync cycle.
   *
   * Falls back to a plain removeItem() if the key does not belong to a bundle
   * main (no _bundleId or _role !== 'main').
   *
   * @param {string} key – line_item.key of the main item to remove
   * @returns {Promise<Object>} final cart state after all removals
   */
  async function removeBundleMain(key) {
    var cart = await fetchCart();
    var items = cart.items || [];

    // Locate the item being removed.
    var mainItem = null;
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === key) { mainItem = items[i]; break; }
    }

    if (!mainItem) {
      return removeItem(key);
    }

    var props = mainItem.properties || {};
    var bundleId = typeof props._bundleId === 'string' ? props._bundleId.trim() : '';
    var role = typeof props._role === 'string' ? props._role.trim().toLowerCase() : '';

    if (!bundleId || role !== 'main') {
      return removeItem(key);
    }

    // Collect all gift keys that share the same _bundleId.
    var giftKeys = [];
    items.forEach(function (item) {
      var p = item.properties || {};
      var bId = typeof p._bundleId === 'string' ? p._bundleId.trim() : '';
      var r = typeof p._role === 'string' ? p._role.trim().toLowerCase() : '';
      if (bId === bundleId && r === 'gift') {
        giftKeys.push(item.key);
      }
    });

    // Remove gifts first, then the main — sequential to keep cart state consistent.
    for (var j = 0; j < giftKeys.length; j++) {
      await removeItem(giftKeys[j]);
    }

    var finalCart = await removeItem(key);

    // Refresh cart drawer and badge.
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
    updateCartCountBadge(finalCart.item_count);

    return finalCart;
  }

  // ---------------------------------------------------------------------------
  // Core sync
  // ---------------------------------------------------------------------------

  /**
   * Inspect the cart, compute required changes, and apply them sequentially.
   *
   * Sequential (not parallel) Shopify calls are intentional: each
   * /cart/change.js response reflects the cart after that specific mutation,
   * ensuring line item keys remain valid across operations.
   *
   * @returns {Promise<Object|null>} Final cart state, or null when already running.
   */
  async function syncCartBundles() {
    if (isExplorerCheckoutActive()) { return null; }
    if (_syncing) { return null; }
    _syncing = true;

    try {
      var cart = await fetchCart();
      var groups = groupByBundle(cart.items || []);

      if (groups.size === 0) { return cart; }

      // Separate Rule A removals (parallel-safe: unique keys, qty→0) from
      // Rule B clamps (sequential: safer when adjusting quantities).
      var removalKeys = [];
      var clamps = [];

      groups.forEach(function (group, bundleId) {
        var hasMain = group.mains.length > 0;

        if (!hasMain) {
          // Rule A: main absent — collect all gift keys for parallel removal.
          group.gifts.forEach(function (gift) {
            removalKeys.push(gift.key);
          });
        } else {
          // Rule B: gifts must have quantity === 1.
          group.gifts.forEach(function (gift) {
            if (gift.quantity > 1) {
              clamps.push({ key: gift.key, quantity: 1 });
            }
          });
        }
      });

      if (!removalKeys.length && !clamps.length) { return cart; }

      // Rule A: remove all orphaned gifts in parallel — each has a unique key,
      // so there are no race conditions on the Shopify side.
      if (removalKeys.length) {
        await Promise.all(removalKeys.map(function (key) {
          return removeItem(key);
        }));
      }

      // Rule B: clamps are uncommon; keep sequential for safety.
      for (var i = 0; i < clamps.length; i++) {
        await updateQuantity(clamps[i].key, clamps[i].quantity);
      }

      var finalCart = await fetchCart();

      // Refresh cart drawer HTML. cart:refresh is not listened to by this module
      // so it won't trigger a re-entrant sync.
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));

      // Update cart-count badge directly. We bypass pubsub here because
      // pubsub.publish has no internal try/catch — CartItems.onCartUpdate throws
      // TypeError when the cart object has no sections key, which would prevent
      // CartCount from receiving the event.
      updateCartCountBadge(finalCart.item_count);

      return finalCart;
    } catch (err) {
      throw err;
    } finally {
      _syncing = false;
    }
  }

  // ---------------------------------------------------------------------------
  // Debounced entry point
  // ---------------------------------------------------------------------------

  /**
   * Debounced wrapper — safe to call on every cart update event.
   * Concurrent calls within DEBOUNCE_DELAY ms are collapsed into one execution.
   */
  function isExplorerCheckoutActive() {
    return !!(window.__DSL_CHECKOUT_ACTIVE__ || window.__DSL_EXPLORER_CTA_IN_FLIGHT__);
  }

  function debouncedSyncCartBundles() {
    if (isExplorerCheckoutActive()) return;
    if (_debounceTimer !== null) {
      clearTimeout(_debounceTimer);
    }
    _debounceTimer = setTimeout(function () {
      _debounceTimer = null;
      syncCartBundles().catch(function () {});
    }, DEBOUNCE_DELAY);
  }

  // ---------------------------------------------------------------------------
  // Fetch interceptor — pre-empts /cart/change.js removals for bundle mains
  // ---------------------------------------------------------------------------

  /**
   * Wraps window.fetch for /cart/change.js:
   *
   * 1) quantity > 1 on a bundle gift line — keep the gift at 1 and POST
   *    /cart/add.js for (quantity − 1) units of the same variant with no line
   *    item properties (paid lines).
   *
   * 2) quantity === 0 on a bundle main — replace with /cart/update.js that
   *    removes each bundle line by line item key (not variant id — see inline comments).
   *
   * All other requests pass through unchanged.
   */
  (function () {
    var _origFetch = window.fetch;
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input
              : (input && typeof input.url === 'string' ? input.url : '');
      var isCartChange = url.indexOf('/cart/change') !== -1; // matches /cart/change and /cart/change.js

      // Not a cart change, or one of our own internal calls — pass straight through.
      if (!isCartChange || _internalChange) return _origFetch.apply(this, arguments);

      // Parse the request body.
      var body;
      try { body = JSON.parse(init && init.body); } catch (e) {}

      if (!body) return _origFetch.apply(this, arguments);

      var itemId = body.id !== undefined ? body.id : body.line; // Recharge uses `line`, theme uses `id`
      var callerSections = body.sections || []; // section IDs the original caller bundled
      var reqQty = Number(body.quantity);

      // Gift line: quantity > 1 → keep gift at 1, add extra units as paid.
      if (reqQty > 1) {
        return fetchCart().then(function (cart) {
          var giftItem = findCartItemById(cart, itemId);
          if (!giftItem) {
            return _origFetch.apply(null, [input, init]);
          }

          var gProps = giftItem.properties || {};
          var gBundleId = (gProps._bundleId || '').trim();
          var gRole = (gProps._role || '').trim().toLowerCase();

          if (!gBundleId || gRole !== 'gift') {
            return _origFetch.apply(null, [input, init]);
          }

          var extraPaid = reqQty - 1;
          var bundledSections = callerSections.length ? callerSections : collectBundledSections();

          // The original /cart/change.js (qty 2) was intercepted and never reached
          // Shopify, so the gift is still at qty 1 on the server — no need to
          // clamp it back with a separate call.
          //
          // 1. Add the extra paid unit.
          // 2. Then do a no-op /cart/change.js (qty 1 on a gift already at 1) with
          //    sections bundled — this syncs the true post-add cart state and returns
          //    the full cart object that CartItems expects.
          return _origFetch.call(null, '/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: giftItem.variant_id, quantity: extraPaid }],
            }),
          }).then(function (addRes) {
            if (!addRes.ok) return addRes;
            // Sync: fetch true post-add cart state with sections so CartItems
            // re-renders with all items (gift at 1 + newly added paid line).
            return _origFetch.call(null, '/cart/change.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({
                id: giftItem.key,
                quantity: 1, // gift is already 1 — no-op, returns full cart + sections
                sections: bundledSections.length ? bundledSections : undefined,
              }),
            });
          });
        });
      }

      // Only intercept removals (quantity === 0) for bundle mains below.
      if (Number(body.quantity) !== 0) return _origFetch.apply(this, arguments);

      // Fetch cart BEFORE letting the original request through.
      return fetchCart().then(function (cart) {
        var item = findCartItemById(cart, itemId);

        if (!item) {
          return _origFetch.apply(null, [input, init]).then(function (res) {
            if (res.ok) syncCartBundles().catch(function () {});
            return res;
          });
        }

        var props    = item.properties || {};
        var bundleId = (props._bundleId || '').trim();
        var role     = (props._role    || '').trim().toLowerCase();

        if (!bundleId || role !== 'main') {
          return _origFetch.apply(null, [input, init]).then(function (res) {
            if (res.ok) syncCartBundles().catch(function () {});
            return res;
          });
        }

        // Bundle main confirmed — collect ALL line keys with the same _bundleId.
        // Must use each line's unique `key` (not variant_id): duplicate variants in the
        // cart (bundle gift + paid extra) share variant_id; variant-keyed updates remove
        // the wrong line or all lines for that variant.
        var updates = {};
        cart.items.forEach(function (ci) {
          var p   = ci.properties || {};
          var bid = (p._bundleId || '').trim();
          if (bid === bundleId) {
            updates[ci.key] = 0;
          }
        });

        // Make the combined call. Pass any sections the original caller wanted
        // so the response includes pre-rendered HTML (same as /cart/change.js).
        _internalChange = true;
        var bundledSections = callerSections.length ? callerSections : collectBundledSections();

        return _origFetch.call(null, '/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates: updates,
            sections: bundledSections.length ? bundledSections : undefined,
          }),
        }).then(function (res) {
          _internalChange = false;

          if (!res.ok) {
            return res;
          }

          // Clone once so we can update page/drawer UI without a full reload.
          res.clone().json().then(function (updatedCart) {
            var isCartPage = !!document.querySelector('main-cart');

            if (
              isCartPage &&
              updatedCart &&
              updatedCart.sections &&
              typeof window.theme !== 'undefined' &&
              window.theme.pubsub &&
              typeof window.theme.pubsub.publish === 'function' &&
              window.theme.pubsub.PUB_SUB_EVENTS &&
              window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate
            ) {
              // Update the cart page in place (no location.reload()).
              window.theme.pubsub.publish(window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-items',
                cart: updatedCart,
              });
            } else {
              // Drawer (or fallback): ask listeners to re-render from section HTML.
              document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
            }

            updateCartCountBadge(updatedCart.item_count);
          }).catch(function () {
            // Fallback refresh even if body parse fails.
            document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { open: false } }));
          });

          return res;
        }).catch(function (err) {
          _internalChange = false;
          throw err;
        });
      });
    };
  })();

  // ---------------------------------------------------------------------------
  // Auto-wire to theme cart events (opt-in via data attribute)
  // ---------------------------------------------------------------------------

  /**
   * If any element on the page has data-cart-bundle-sync="auto", the engine
   * automatically listens for the theme's "cart:updated" DOM event and runs
   * debouncedSyncCartBundles on each firing.
   *
   * Add to any liquid layout/section:
   *   <div data-cart-bundle-sync="auto" hidden></div>
   */
  function maybeAutoWire() {
    var optIn = document.querySelector('[data-cart-bundle-sync="auto"]');
    if (!optIn) return;

    document.addEventListener('cart:updated', function () {
      debouncedSyncCartBundles();
    });

    // Also handle theme.pubsub cartUpdate if available.
    if (
      typeof window.theme !== 'undefined' &&
      window.theme.pubsub &&
      window.theme.pubsub.subscribe &&
      window.theme.pubsub.PUB_SUB_EVENTS &&
      window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate
    ) {
      window.theme.pubsub.subscribe(
        window.theme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        function () {
          debouncedSyncCartBundles();
        }
      );
    }
  }

  // Guard: if DOMContentLoaded has already fired (possible when this script is
  // injected late via a section <script> tag), run immediately.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', maybeAutoWire);
  } else {
    maybeAutoWire();
  }


  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  window.CartBundleSync = {
    sync: syncCartBundles,
    debouncedSync: debouncedSyncCartBundles,
    groupByBundle: groupByBundle,
    removeItem: removeItem,
    removeBundleMain: removeBundleMain,
    updateQuantity: updateQuantity,
  };
})();
